import type { PanelTemplate } from "./types.ts";

/**
 * Fyra grundmallar för typiska svenska anläggningar. Komponent-koderna
 * (DB16, JFA16, LB40 osv) tolkas via @electro-planner/components när mallen
 * appliceras, så katalogvärden (poles, characteristic, sensitivity) kommer
 * in automatiskt.
 */

export const TEMPLATES: ReadonlyArray<PanelTemplate> = [
  {
    key: "apartment-1rok",
    name: "Lägenhet 1 rum + kök",
    description:
      "Grundpanel för en mindre lägenhet. 25 A huvudsäkring, central jordfelsbrytare och separata kretsar för uttag, belysning, spis och tvätt.",
    panel: {
      designation: "CK1",
      title: "Lägenhet — 1 rum + kök",
      busbarAmps: 63,
    },
    sections: [
      {
        code: "S1",
        label: "Huvudcentral",
        rows: [
          { designation: "Q1", componentCode: "LB40", usage: "Huvudbrytare" },
          {
            designation: "F0",
            componentCode: "JFB40",
            usage: "Central jordfelsbrytare 30 mA",
          },
        ],
      },
      {
        code: "S2",
        label: "Belysning + uttag",
        rows: [
          {
            designation: "F1",
            componentCode: "DB10",
            cableCode: "3G1,5",
            usage: "Belysning allrum/sovrum",
          },
          {
            designation: "F2",
            componentCode: "DB10",
            cableCode: "3G1,5",
            usage: "Belysning kök + hall",
          },
          {
            designation: "F3",
            componentCode: "DB16",
            cableCode: "3G2,5",
            usage: "Uttag allrum",
          },
          {
            designation: "F4",
            componentCode: "DB16",
            cableCode: "3G2,5",
            usage: "Uttag hall + sovrum",
          },
          {
            designation: "F5",
            componentCode: "DB16",
            cableCode: "3G2,5",
            usage: "Uttag kök bänk",
          },
          {
            designation: "F6",
            componentCode: "DB16",
            cableCode: "3G2,5",
            usage: "Badrum — JFB krav",
          },
        ],
      },
      {
        code: "S3",
        label: "Fast installation",
        rows: [
          {
            designation: "F7",
            componentCode: "DB16",
            poles: 3,
            cableCode: "5G2,5",
            usage: "Spis",
            loadWatts: 7200,
          },
          {
            designation: "F8",
            componentCode: "DB10",
            cableCode: "3G1,5",
            usage: "Tvättmaskin",
            loadWatts: 2200,
          },
          {
            designation: "F9",
            componentCode: "DB10",
            cableCode: "3G1,5",
            usage: "Diskmaskin",
            loadWatts: 2000,
          },
        ],
      },
    ],
  },

  {
    key: "villa-150",
    name: "Villa 150 m²",
    description:
      "Normalstor villa med separata belysnings- och uttagskretsar per rum, pannhus, utomhus och garage. 25 A huvudsäkring per fas, 125 A skena.",
    panel: {
      designation: "CH",
      title: "Huvudcentral — villa 150 m²",
      busbarAmps: 125,
    },
    sections: [
      {
        code: "S1",
        label: "Huvud + mätning",
        rows: [
          { designation: "Q1", componentCode: "LB63", usage: "Huvudbrytare" },
          {
            designation: "F0",
            componentCode: "JFB63",
            usage: "Central jordfelsbrytare",
          },
        ],
      },
      {
        code: "S2",
        label: "Belysning",
        rows: [
          { designation: "F1", componentCode: "DB10", cableCode: "3G1,5", usage: "Belysning vardagsrum" },
          { designation: "F2", componentCode: "DB10", cableCode: "3G1,5", usage: "Belysning kök + matplats" },
          { designation: "F3", componentCode: "DB10", cableCode: "3G1,5", usage: "Belysning sovrum 1-3" },
          { designation: "F4", componentCode: "DB10", cableCode: "3G1,5", usage: "Belysning entré + hall" },
          { designation: "F5", componentCode: "DB10", cableCode: "3G1,5", usage: "Belysning badrum" },
          { designation: "F6", componentCode: "JFA10", cableCode: "3G1,5", usage: "Belysning ute / trädgård" },
        ],
      },
      {
        code: "S3",
        label: "Uttag",
        rows: [
          { designation: "F10", componentCode: "DB16", cableCode: "3G2,5", usage: "Uttag vardagsrum" },
          { designation: "F11", componentCode: "DB16", cableCode: "3G2,5", usage: "Uttag kök bänk 1" },
          { designation: "F12", componentCode: "DB16", cableCode: "3G2,5", usage: "Uttag kök bänk 2" },
          { designation: "F13", componentCode: "DB16", cableCode: "3G2,5", usage: "Uttag sovrum" },
          { designation: "F14", componentCode: "JFA16", cableCode: "3G2,5", usage: "Uttag badrum" },
          { designation: "F15", componentCode: "JFA16", cableCode: "3G2,5", usage: "Uttag ute / terrass" },
          { designation: "F16", componentCode: "JFA16", cableCode: "3G2,5", usage: "Uttag garage" },
        ],
      },
      {
        code: "S4",
        label: "Fast installation",
        rows: [
          { designation: "F20", componentCode: "DB16", poles: 3, cableCode: "5G2,5", usage: "Spis", loadWatts: 7200 },
          { designation: "F21", componentCode: "DB16", poles: 3, cableCode: "5G2,5", usage: "Bastu", loadWatts: 6000 },
          { designation: "F22", componentCode: "DB10", cableCode: "3G1,5", usage: "Tvättmaskin" },
          { designation: "F23", componentCode: "DB10", cableCode: "3G1,5", usage: "Torktumlare" },
          { designation: "F24", componentCode: "DB10", cableCode: "3G1,5", usage: "Diskmaskin" },
          { designation: "F25", componentCode: "DB10", cableCode: "3G1,5", usage: "Värmepump kompressor" },
          { designation: "F26", componentCode: "DB25", poles: 3, cableCode: "5G6", usage: "EV-laddbox 11 kW", loadWatts: 11000 },
        ],
      },
    ],
  },

  {
    key: "industri-ls",
    name: "Industri — lågspänning",
    description:
      "Industri-grupp-central för mindre verkstad: motorer, belysning, motorutlopp och uttag. 125 A skena, MCCB som huvudbrytare.",
    panel: {
      designation: "GC1",
      title: "Grupp-central — verkstad",
      busbarAmps: 125,
      enclosure: "Rittal AX 800×600",
    },
    sections: [
      {
        code: "S1",
        label: "Huvud + kompensering",
        rows: [
          { designation: "Q1", componentCode: "EB125", usage: "Huvudbrytare" },
          { designation: "F0", componentCode: "JFB125", usage: "Huvud-JFB 300 mA (brand)" },
        ],
      },
      {
        code: "S2",
        label: "Motorer",
        rows: [
          { designation: "M1", componentCode: "DB16", characteristic: "C", poles: 3, cableCode: "5G2,5", usage: "Motor 1 — kompressor 5,5 kW", loadWatts: 5500 },
          { designation: "M2", componentCode: "DB16", characteristic: "C", poles: 3, cableCode: "5G2,5", usage: "Motor 2 — fläkt 4 kW", loadWatts: 4000 },
          { designation: "M3", componentCode: "DB10", characteristic: "C", poles: 3, cableCode: "5G1,5", usage: "Motor 3 — pump 2,2 kW", loadWatts: 2200 },
        ],
      },
      {
        code: "S3",
        label: "Belysning + uttag",
        rows: [
          { designation: "F10", componentCode: "DB10", cableCode: "3G1,5", usage: "Belysning verkstad" },
          { designation: "F11", componentCode: "DB10", cableCode: "3G1,5", usage: "Belysning kontor" },
          { designation: "F12", componentCode: "JFA16", cableCode: "3G2,5", usage: "Uttag verkstad — JFB krav" },
          { designation: "F13", componentCode: "DB16", poles: 3, cableCode: "5G2,5", usage: "3-fas-uttag motor-utlopp" },
        ],
      },
    ],
  },

  {
    key: "solar-micro",
    name: "Solceller — mikroinverter",
    description:
      "AC-central för solcellsanläggning med mikroinverter per panel. AC-brytning, DC-skyddsapparat skrivs in manuellt vid behov.",
    panel: {
      designation: "CPV",
      title: "Solcells-AC-central",
      busbarAmps: 40,
    },
    sections: [
      {
        code: "S1",
        label: "AC-matning",
        rows: [
          { designation: "Q1", componentCode: "LB40", usage: "AC-huvudbrytare, plomberbar" },
          { designation: "F0", componentCode: "JFB40", usage: "JFB typ A 30 mA" },
        ],
      },
      {
        code: "S2",
        label: "Strängar",
        rows: [
          { designation: "F1", componentCode: "DB16", poles: 3, cableCode: "5G2,5", usage: "Sträng 1 — mikroinv. x12" },
          { designation: "F2", componentCode: "DB16", poles: 3, cableCode: "5G2,5", usage: "Sträng 2 — mikroinv. x12" },
        ],
      },
      {
        code: "S3",
        label: "Mätning + styr",
        rows: [
          { designation: "F10", componentCode: "DB6", cableCode: "3G1,5", usage: "Energimätare / smart meter" },
          { designation: "F11", componentCode: "DB6", cableCode: "3G1,5", usage: "Kommunikationsdosa" },
        ],
      },
    ],
  },
];

export function findTemplate(key: string): PanelTemplate | undefined {
  return TEMPLATES.find((t) => t.key === key);
}
